// @ts-check
import { useState } from 'react';
import { useToast } from '../../../context/ToastContext';
import adminDashboardService from '../../../services/adminDashboardService';
import BlockCard from '../BlockCard';

const formatDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

/** Newsletter welcome codes: signed up vs redeemed bar, and unredeemed codes expiring within 3 days with a one-time Remind. */
export default function WelcomeDiscountBlock({ block }) {
  const toast = useToast();
  const { signed_up: signedUp = 0, redeemed = 0, not_redeemed: notRedeemed = 0, expiring_soon: expiring = [] } = block.data || {};
  const [sent, setSent] = useState(() => new Set());
  const [sending, setSending] = useState(() => new Set());
  const redeemedPct = signedUp > 0 ? (redeemed / signedUp) * 100 : 0;

  const remind = async (id) => {
    setSending((s) => new Set(s).add(id));
    try {
      await adminDashboardService.sendWelcomeReminder(id);
      setSent((s) => new Set(s).add(id));
    } catch (err) {
      // 409 "already sent": someone else reminded this code first, so show it as sent.
      // Other 409s (redeemed, expired) and send failures leave the button usable.
      if (err?.status === 409 && /already sent/i.test(err?.message || '')) setSent((s) => new Set(s).add(id));
      toast.error(err?.message || 'Failed to send reminder');
    } finally {
      setSending((s) => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
    }
  };

  return (
    <BlockCard title="Welcome discount" href={block.href} headline={block.headline}>
      <div data-testid="welcome-bar">
        <div className="flex h-2 overflow-hidden rounded bg-muted" aria-hidden="true">
          <div className="h-2 bg-green-500 dark:bg-green-400" style={{ width: `${redeemedPct}%` }} />
        </div>
        <p className="mt-2 flex flex-wrap gap-x-4 text-sm text-muted-foreground">
          <span>Signed up {signedUp}</span>
          <span>Redeemed {redeemed}</span>
          <span>Not redeemed {notRedeemed}</span>
        </p>
      </div>
      <h4 className="mt-4 text-xs font-medium uppercase tracking-wide text-muted-foreground">Expiring in 3 days</h4>
      {expiring.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">No unredeemed codes expiring in the next 3 days</p>
      ) : (
        <ul className="mt-2 divide-y divide-border" data-testid="welcome-expiring">
          {expiring.map((row) => {
            const done = row.reminded || sent.has(row.id);
            const busy = sending.has(row.id);
            return (
              <li key={row.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <div className="min-w-0">
                  <p className="truncate">{row.email}</p>
                  <p className="text-xs text-muted-foreground">
                    {row.code} · expires {formatDate(row.expires_at)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => remind(row.id)}
                  disabled={done || busy}
                  className="shrink-0 rounded-md border border-border px-3 py-1 text-xs font-medium hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {done ? 'Sent' : 'Remind'}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </BlockCard>
  );
}
